import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { adminAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import RealtimeKPIs from '@/components/admin/RealtimeKPIs';
import AdminCharts from '@/components/admin/AdminCharts';
import EnhancedUsersList from '@/components/admin/EnhancedUsersList';
import AdminMap from '@/components/admin/AdminMap';
import AdminAlerts from '@/components/admin/AdminAlerts';
import ExportCenter from '@/components/admin/ExportCenter';
import HealthDashboard from '@/components/admin/HealthDashboard';
import PushNotifications from '@/components/admin/PushNotifications';
import AgentTracking from '@/components/admin/AgentTracking';
import {
  Users,
  Stethoscope,
  Calendar,
  Wallet,
  Check,
  X,
  AlertCircle,
  Loader2,
  Shield,
  Activity,
  Building2,
  Plus,
  Pencil,
  Trash2,
  FileCheck,
  Eye,
  MapPin,
  Phone,
  Image,
  Video,
  Clock,
  Upload,
  Link as LinkIcon,
  FileText,
  Download,
  ExternalLink,
  BarChart3,
  Map,
  FileSpreadsheet,
  Bell,
  Heart,
  UserCheck
} from 'lucide-react';
import { toast } from 'sonner';

const facilityTypeLabels = {
  hospital: 'Hôpital',
  clinic: 'Clinique',
  pharmacy: 'Pharmacie',
  laboratory: 'Laboratoire',
  imaging_center: "Centre d'imagerie",
  maternity: 'Maternité',
  dental_clinic: 'Cabinet dentaire',
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingProfessionals, setPendingProfessionals] = useState([]);
  const [users, setUsers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [pendingFacilityDocs, setPendingFacilityDocs] = useState([]);
  const [pendingKYC, setPendingKYC] = useState([]);
  const [selectedKYC, setSelectedKYC] = useState(null);
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [kycModalTab, setKycModalTab] = useState('profile'); // New: Tab for KYC modal
  const [loadingKYCDetail, setLoadingKYCDetail] = useState(false); // New: Loading state for KYC detail
  
  // Facility form state
  const [showFacilityModal, setShowFacilityModal] = useState(false);
  const [facilityModalTab, setFacilityModalTab] = useState('info');
  const [facilityForm, setFacilityForm] = useState({
    name: '',
    facility_type: '',
    description: '',
    address: '',
    city: 'Yaoundé',
    phone: '',
    email: '',
    website: '',
    emergency_24h: false,
    has_ambulance: false,
    is_verified: true,
    always_open: false,
    // New enriched fields
    profile_photo: null,
    cover_photo: null,
    youtube_url: '',
    specialties: [],
    services: [],
    insurance_accepted: [],
    opening_hours: {
      monday: { open: '08:00', close: '18:00', closed: false },
      tuesday: { open: '08:00', close: '18:00', closed: false },
      wednesday: { open: '08:00', close: '18:00', closed: false },
      thursday: { open: '08:00', close: '18:00', closed: false },
      friday: { open: '08:00', close: '18:00', closed: false },
      saturday: { open: '08:00', close: '14:00', closed: false },
      sunday: { open: '', close: '', closed: true },
    },
    gallery: [],
  });
  const [editingFacilityId, setEditingFacilityId] = useState(null);
  const [savingFacility, setSavingFacility] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newSpecialty, setNewSpecialty] = useState('');
  const [newService, setNewService] = useState('');
  const [newInsurance, setNewInsurance] = useState('');
  const profilePhotoRef = useRef(null);
  const coverPhotoRef = useRef(null);
  const galleryPhotoRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, pendingRes, usersRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getPendingProfessionals(),
        adminAPI.getUsers(),
      ]);
      
      setStats(statsRes.data);
      setPendingProfessionals(pendingRes.data.professionals);
      setUsers(usersRes.data.users);
      
      // Load facilities
      try {
        const facilitiesRes = await api.get('/facilities');
        setFacilities(facilitiesRes.data.facilities || []);
      } catch (e) {
        console.log('No facilities endpoint or empty');
      }
      
      // Load pending facility documents
      try {
        const pendingDocsRes = await api.get('/admin/facility-documents/pending');
        setPendingFacilityDocs(pendingDocsRes.data.documents || []);
      } catch (e) {
        console.log('No pending docs endpoint');
      }
      
      // Load pending KYC requests
      try {
        const kycRes = await api.get('/kyc/admin/pending');
        setPendingKYC(kycRes.data.kyc_records || []);
      } catch (e) {
        console.log('No KYC pending endpoint');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleValidation = async (profileId, approved) => {
    try {
      await adminAPI.validateProfessional(profileId, approved);
      toast.success(approved ? 'Professionnel validé' : 'Professionnel rejeté');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleSuspend = async (userId, suspended) => {
    try {
      await adminAPI.suspendUser(userId, suspended);
      toast.success(suspended ? 'Utilisateur suspendu' : 'Utilisateur réactivé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const openFacilityModal = (facility = null) => {
    setFacilityModalTab('info');
    if (facility) {
      setFacilityForm({
        name: facility.name || '',
        facility_type: facility.facility_type || '',
        description: facility.description || '',
        address: facility.address || '',
        city: facility.city || 'Yaoundé',
        phone: facility.phone || '',
        email: facility.email || '',
        website: facility.website || '',
        emergency_24h: facility.emergency_24h || false,
        has_ambulance: facility.has_ambulance || false,
        is_verified: facility.is_verified !== false,
        always_open: facility.always_open || false,
        // Enriched fields
        profile_photo: facility.profile_photo || null,
        cover_photo: facility.cover_photo || null,
        youtube_url: facility.youtube_url || '',
        specialties: facility.specialties || [],
        services: facility.services || [],
        insurance_accepted: facility.insurance_accepted || [],
        opening_hours: facility.opening_hours || {
          monday: { open: '08:00', close: '18:00', closed: false },
          tuesday: { open: '08:00', close: '18:00', closed: false },
          wednesday: { open: '08:00', close: '18:00', closed: false },
          thursday: { open: '08:00', close: '18:00', closed: false },
          friday: { open: '08:00', close: '18:00', closed: false },
          saturday: { open: '08:00', close: '14:00', closed: false },
          sunday: { open: '', close: '', closed: true },
        },
        gallery: facility.gallery || [],
      });
      setEditingFacilityId(facility.id);
    } else {
      setFacilityForm({
        name: '',
        facility_type: '',
        description: '',
        address: '',
        city: 'Yaoundé',
        phone: '',
        email: '',
        website: '',
        emergency_24h: false,
        has_ambulance: false,
        is_verified: true,
        profile_photo: null,
        cover_photo: null,
        youtube_url: '',
        specialties: [],
        services: [],
        insurance_accepted: [],
        opening_hours: {
          monday: { open: '08:00', close: '18:00', closed: false },
          tuesday: { open: '08:00', close: '18:00', closed: false },
          wednesday: { open: '08:00', close: '18:00', closed: false },
          thursday: { open: '08:00', close: '18:00', closed: false },
          friday: { open: '08:00', close: '18:00', closed: false },
          saturday: { open: '08:00', close: '14:00', closed: false },
          sunday: { open: '', close: '', closed: true },
        },
        gallery: [],
        always_open: false,
      });
      setEditingFacilityId(null);
    }
    setShowFacilityModal(true);
  };

  // Helper functions for facility form
  const handlePhotoUpload = async (file, type) => {
    if (!file) return;
    if (!editingFacilityId) {
      toast.error('Veuillez d\'abord créer l\'établissement avant d\'ajouter des photos');
      return;
    }
    
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const endpoint = type === 'gallery' 
        ? `/facility-profile/gallery/add`
        : `/facility-profile/upload-photo/${type}`;
      
      // Note: This requires the facility user to be logged in
      // For admin, we'll store the URL directly
      const response = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (type === 'gallery') {
        setFacilityForm(p => ({
          ...p,
          gallery: [...p.gallery, response.data.photo]
        }));
      } else {
        const fieldName = type === 'profile' ? 'profile_photo' : 'cover_photo';
        setFacilityForm(p => ({ ...p, [fieldName]: response.data.url }));
      }
      toast.success('Photo téléchargée');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors du téléchargement. L\'admin doit utiliser le dashboard établissement.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const addToList = (listName, value, setValue) => {
    if (!value.trim()) return;
    if (facilityForm[listName].length >= 20) {
      toast.error('Maximum 20 éléments');
      return;
    }
    if (!facilityForm[listName].includes(value.trim())) {
      setFacilityForm(p => ({
        ...p,
        [listName]: [...p[listName], value.trim()]
      }));
    }
    setValue('');
  };

  const removeFromList = (listName, index) => {
    setFacilityForm(p => ({
      ...p,
      [listName]: p[listName].filter((_, i) => i !== index)
    }));
  };

  const updateOpeningHours = (day, field, value) => {
    setFacilityForm(p => ({
      ...p,
      opening_hours: {
        ...p.opening_hours,
        [day]: {
          ...p.opening_hours[day],
          [field]: value
        }
      }
    }));
  };

  const dayLabels = {
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    saturday: 'Samedi',
    sunday: 'Dimanche'
  };

  const handleSaveFacility = async (e) => {
    e.preventDefault();
    if (!facilityForm.name || !facilityForm.facility_type || !facilityForm.address) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setSavingFacility(true);
    try {
      if (editingFacilityId) {
        await api.put(`/facilities/${editingFacilityId}`, facilityForm);
        toast.success('Établissement mis à jour');
      } else {
        await api.post('/facilities', facilityForm);
        toast.success('Établissement créé');
      }
      setShowFacilityModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving facility:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingFacility(false);
    }
  };

  const handleDeleteFacility = async (facilityId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet établissement ?')) {
      return;
    }
    
    try {
      await api.delete(`/facilities/${facilityId}`);
      toast.success('Établissement supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDocumentReview = async (docId, status, notes = '') => {
    try {
      await api.put(`/admin/facility-documents/${docId}/review`, {
        status,
        reviewer_notes: notes
      });
      toast.success(status === 'approved' ? 'Document approuvé' : 'Document rejeté');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const openKYCDetail = async (kyc) => {
    setSelectedKYC(kyc);
    setRejectReason('');
    setKycModalTab('profile');
    setShowKYCModal(true);
    
    // Load full KYC details including profile
    setLoadingKYCDetail(true);
    try {
      const response = await api.get(`/kyc/admin/${kyc.id}`);
      if (response.data.kyc) {
        setSelectedKYC(response.data.kyc);
      }
    } catch (error) {
      console.error('Error loading KYC detail:', error);
      // Keep basic info if detail fetch fails
    } finally {
      setLoadingKYCDetail(false);
    }
  };

  const handleKYCReview = async (status) => {
    if (!selectedKYC) return;
    
    try {
      await api.put(`/kyc/admin/${selectedKYC.id}/status`, {
        status,
        reviewer_notes: status === 'rejected' ? rejectReason : ''
      });
      toast.success(status === 'approved' ? 'KYC approuvé' : 'KYC rejeté');
      setShowKYCModal(false);
      setSelectedKYC(null);
      loadData();
    } catch (error) {
      console.error('KYC review error:', error);
      toast.error('Erreur lors de la validation KYC');
    }
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      'identity_card': 'Pièce d\'identité',
      'diploma': 'Diplôme',
      'license': 'Autorisation d\'exercice',
      'creation_authorization': 'Autorisation de création',
      'operating_license': 'Licence d\'exploitation',
      'tax_documents': 'Documents fiscaux',
      'other': 'Autre'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  // Count facilities by role
  const facilityUsers = users.filter(u => u.role === 'facility');

  return (
    <div className="section-padding py-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <Shield className="w-5 h-5 text-purple-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Administration
          </h1>
        </div>
        <p className="text-slate-600">Bienvenue, {user?.full_name}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-4 px-4 mb-6">
          <TabsList className="inline-flex min-w-max">
            <TabsTrigger value="overview" className="text-xs sm:text-sm whitespace-nowrap">
              <Activity className="w-4 h-4 mr-1 hidden sm:inline" />
              Vue d&apos;ensemble
            </TabsTrigger>
            <TabsTrigger value="validations" className="text-xs sm:text-sm whitespace-nowrap">
              <Check className="w-4 h-4 mr-1 hidden sm:inline" />
              Validations
              {pendingProfessionals.length > 0 && (
                <Badge className="ml-1 sm:ml-2 bg-red-500 text-white text-xs">{pendingProfessionals.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="kyc" className="text-xs sm:text-sm whitespace-nowrap">
              <FileCheck className="w-4 h-4 mr-1 hidden sm:inline" />
              KYC
              {pendingKYC.length > 0 && (
                <Badge className="ml-1 sm:ml-2 bg-amber-500 text-white text-xs">{pendingKYC.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="facilities" data-testid="facilities-tab" className="text-xs sm:text-sm whitespace-nowrap">
              <Building2 className="w-4 h-4 mr-1 hidden sm:inline" />
              Établissements
            </TabsTrigger>
            <TabsTrigger value="users" className="text-xs sm:text-sm whitespace-nowrap">
              <Users className="w-4 h-4 mr-1 hidden sm:inline" />
              Utilisateurs
            </TabsTrigger>
            <TabsTrigger value="map" className="text-xs sm:text-sm whitespace-nowrap">
              <Map className="w-4 h-4 mr-1 hidden sm:inline" />
              Carte
            </TabsTrigger>
            <TabsTrigger value="export" className="text-xs sm:text-sm whitespace-nowrap">
              <FileSpreadsheet className="w-4 h-4 mr-1 hidden sm:inline" />
              Export
            </TabsTrigger>
            <TabsTrigger value="health" className="text-xs sm:text-sm whitespace-nowrap">
              <Heart className="w-4 h-4 mr-1 hidden sm:inline" />
              Santé
            </TabsTrigger>
            <TabsTrigger value="notifications" className="text-xs sm:text-sm whitespace-nowrap">
              <Bell className="w-4 h-4 mr-1 hidden sm:inline" />
              Notifs
            </TabsTrigger>
            <TabsTrigger value="agents" className="text-xs sm:text-sm whitespace-nowrap">
              <UserCheck className="w-4 h-4 mr-1 hidden sm:inline" />
              Agents
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview">
          {/* Real-time KPIs */}
          <div className="space-y-8">
            {/* Alerts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <RealtimeKPIs />
              </div>
              <div>
                <AdminAlerts onNavigate={(tab) => setActiveTab(tab)} />
              </div>
            </div>
            
            {/* Charts Section */}
            <AdminCharts />
          </div>
        </TabsContent>

        <TabsContent value="validations">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Professionnels en attente de validation</CardTitle>
            </CardHeader>
            <CardContent>
              {pendingProfessionals.length > 0 ? (
                <div className="space-y-4">
                  {pendingProfessionals.map((prof) => (
                    <div
                      key={prof.id}
                      className="flex items-start justify-between p-4 rounded-xl bg-slate-50"
                      data-testid={`pending-professional-${prof.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                          <Stethoscope className="w-6 h-6 text-red-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{prof.user?.full_name}</h3>
                          <p className="text-sm text-red-500">{prof.specialty}</p>
                          <p className="text-sm text-slate-500">{prof.user?.phone}</p>
                          {prof.license_number && (
                            <p className="text-xs text-slate-400 mt-1">Licence: {prof.license_number}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleValidation(prof.id, false)}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                          data-testid={`reject-${prof.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleValidation(prof.id, true)}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`approve-${prof.id}`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Check className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucune validation en attente</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* KYC Tab */}
        <TabsContent value="kyc">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <FileCheck className="w-5 h-5 text-amber-600" />
                Demandes de vérification KYC
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {pendingKYC.length} demande(s) en attente de validation
              </CardDescription>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              {pendingKYC.length > 0 ? (
                <div className="space-y-3">
                  {pendingKYC.map((kyc) => (
                    <div
                      key={kyc.id}
                      className="p-3 sm:p-4 rounded-xl bg-slate-50 border"
                      data-testid={`pending-kyc-${kyc.id}`}
                    >
                      {/* Header with user info */}
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          kyc.user_role === 'facility' ? 'bg-indigo-100' : 'bg-red-100'
                        }`}>
                          {kyc.user_role === 'facility' ? (
                            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                          ) : (
                            <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 text-sm sm:text-base truncate">{kyc.user_name}</h3>
                          <p className="text-xs sm:text-sm text-slate-500 truncate">{kyc.user_email}</p>
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-1">
                            <Badge className={`text-xs ${
                              kyc.user_role === 'facility' 
                                ? 'bg-indigo-100 text-indigo-700' 
                                : 'bg-red-100 text-red-600'
                            }`}>
                              {kyc.user_role === 'facility' ? 'Établissement' : 'Pro'}
                            </Badge>
                            <span className="text-xs text-slate-400">
                              {kyc.documents?.length || 0} doc(s)
                            </span>
                            <span className="text-xs text-slate-400 hidden sm:inline">
                              {new Date(kyc.submitted_at || kyc.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action buttons - Always visible */}
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-200">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openKYCDetail(kyc)}
                          className="flex-1 sm:flex-none text-xs sm:text-sm h-8 sm:h-9"
                          data-testid={`view-kyc-${kyc.id}`}
                        >
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          Détails
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedKYC(kyc);
                            handleKYCReview('rejected');
                          }}
                          className="flex-1 sm:flex-none text-xs sm:text-sm h-8 sm:h-9 border-red-200 text-red-600 hover:bg-red-50"
                          data-testid={`quick-reject-kyc-${kyc.id}`}
                        >
                          <X className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          Refuser
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedKYC(kyc);
                            handleKYCReview('approved');
                          }}
                          className="flex-1 sm:flex-none text-xs sm:text-sm h-8 sm:h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`quick-approve-kyc-${kyc.id}`}
                        >
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          Approuver
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <FileCheck className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm sm:text-base">Aucune demande KYC en attente</p>
                  <p className="text-xs sm:text-sm text-slate-400 mt-1">
                    Les demandes de vérification apparaîtront ici
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Facilities Tab */}
        <TabsContent value="facilities">
          <div className="space-y-6">
            {/* Add Facility Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Gestion des établissements</h2>
                <p className="text-sm text-slate-500">Ajoutez et gérez les établissements de santé</p>
              </div>
              <Button
                onClick={() => openFacilityModal()}
                className="bg-[#E53935] hover:bg-[#C62828]"
                data-testid="add-facility-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un établissement
              </Button>
            </div>

            {/* Facilities List */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Liste des établissements</CardTitle>
                <CardDescription>
                  {facilities.length} établissement(s) enregistré(s)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {facilities.length > 0 ? (
                  <div className="space-y-4">
                    {facilities.map((facility) => (
                      <div
                        key={facility.id}
                        className="flex items-start justify-between p-4 rounded-xl bg-slate-50"
                        data-testid={`facility-${facility.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-indigo-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{facility.name}</h3>
                            <Badge className="mt-1 bg-indigo-100 text-indigo-700" data-testid={`facility-type-badge-${facility.id}`}>
                              {facilityTypeLabels[facility.facility_type] || facility.facility_type}
                            </Badge>
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {facility.city}
                              </span>
                              {facility.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {facility.phone}
                                </span>
                              )}
                            </div>
                            <div className="flex gap-2 mt-2">
                              {facility.emergency_24h && (
                                <Badge className="bg-red-100 text-red-700 text-xs">Urgences 24h</Badge>
                              )}
                              {facility.has_ambulance && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">Ambulance</Badge>
                              )}
                              <Badge className={facility.is_verified 
                                ? "bg-green-100 text-green-700 text-xs" 
                                : "bg-yellow-100 text-yellow-700 text-xs"
                              }>
                                {facility.is_verified ? 'Vérifié' : 'Non vérifié'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openFacilityModal(facility)}
                            data-testid={`edit-facility-${facility.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteFacility(facility.id)}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            data-testid={`delete-facility-${facility.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 mb-4">Aucun établissement enregistré</p>
                    <Button onClick={() => openFacilityModal()} className="bg-[#E53935] hover:bg-[#C62828]">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter le premier établissement
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <EnhancedUsersList />
        </TabsContent>

        <TabsContent value="map">
          <AdminMap />
        </TabsContent>

        <TabsContent value="export">
          <ExportCenter />
        </TabsContent>

        <TabsContent value="health">
          <HealthDashboard />
        </TabsContent>

        <TabsContent value="notifications">
          <PushNotifications />
        </TabsContent>

        <TabsContent value="agents">
          <AgentTracking />
        </TabsContent>
      </Tabs>

      {/* Facility Modal - Enriched Version */}
      <Dialog open={showFacilityModal} onOpenChange={setShowFacilityModal}>
        <DialogContent className="max-w-3xl w-[95vw] sm:w-full max-h-[85vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2 flex-shrink-0">
            <DialogTitle className="text-base sm:text-lg">
              {editingFacilityId ? 'Modifier l\'établissement' : 'Ajouter un établissement'}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Remplissez les informations complètes de l&apos;établissement de santé
            </DialogDescription>
          </DialogHeader>

          <Tabs value={facilityModalTab} onValueChange={setFacilityModalTab} className="flex-1 overflow-hidden flex flex-col px-4 sm:px-6">
            <TabsList className="grid grid-cols-4 w-full flex-shrink-0">
              <TabsTrigger value="info" className="text-xs sm:text-sm px-1 sm:px-3">
                <Building2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                <span className="hidden sm:inline">Infos</span>
              </TabsTrigger>
              <TabsTrigger value="media" className="text-xs sm:text-sm px-1 sm:px-3">
                <Image className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                <span className="hidden sm:inline">Médias</span>
              </TabsTrigger>
              <TabsTrigger value="services" className="text-xs sm:text-sm px-1 sm:px-3">
                <Stethoscope className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                <span className="hidden sm:inline">Services</span>
              </TabsTrigger>
              <TabsTrigger value="hours" className="text-xs sm:text-sm px-1 sm:px-3">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                <span className="hidden sm:inline">Horaires</span>
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto pb-4" style={{ maxHeight: 'calc(85vh - 200px)' }}>
              <form onSubmit={handleSaveFacility} className="space-y-4 py-4">
                {/* Tab 1: Basic Info */}
                <TabsContent value="info" className="mt-0 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label>Nom de l&apos;établissement *</Label>
                      <Input
                        value={facilityForm.name}
                        onChange={(e) => setFacilityForm(p => ({ ...p, name: e.target.value }))}
                        placeholder="Ex: Hôpital Central de Yaoundé"
                        data-testid="facility-name-input"
                      />
                    </div>

                    <div>
                      <Label>Type d&apos;établissement *</Label>
                      <Select
                        value={facilityForm.facility_type}
                        onValueChange={(v) => setFacilityForm(p => ({ ...p, facility_type: v }))}
                      >
                        <SelectTrigger data-testid="facility-type-select">
                          <SelectValue placeholder="Sélectionnez le type" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(facilityTypeLabels).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={facilityForm.description}
                      onChange={(e) => setFacilityForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Décrivez l'établissement, ses points forts..."
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label>Adresse *</Label>
                    <Input
                      value={facilityForm.address}
                      onChange={(e) => setFacilityForm(p => ({ ...p, address: e.target.value }))}
                      placeholder="Ex: Rue 123, Quartier Bastos"
                      data-testid="facility-address-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Ville *</Label>
                      <Select
                        value={facilityForm.city}
                        onValueChange={(v) => setFacilityForm(p => ({ ...p, city: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yaoundé">Yaoundé</SelectItem>
                          <SelectItem value="Douala">Douala</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Téléphone</Label>
                      <Input
                        value={facilityForm.phone}
                        onChange={(e) => setFacilityForm(p => ({ ...p, phone: e.target.value }))}
                        placeholder="+237 6XX XXX XXX"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={facilityForm.email}
                        onChange={(e) => setFacilityForm(p => ({ ...p, email: e.target.value }))}
                        placeholder="contact@etablissement.cm"
                      />
                    </div>
                    <div>
                      <Label>Site web</Label>
                      <Input
                        value={facilityForm.website}
                        onChange={(e) => setFacilityForm(p => ({ ...p, website: e.target.value }))}
                        placeholder="https://www.etablissement.cm"
                      />
                    </div>
                  </div>

                  <div className="space-y-3 pt-2">
                    <div className="flex items-center justify-between">
                      <Label className="cursor-pointer">Urgences 24h/24</Label>
                      <Switch
                        checked={facilityForm.emergency_24h}
                        onCheckedChange={(v) => setFacilityForm(p => ({ ...p, emergency_24h: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="cursor-pointer">Service ambulance</Label>
                      <Switch
                        checked={facilityForm.has_ambulance}
                        onCheckedChange={(v) => setFacilityForm(p => ({ ...p, has_ambulance: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="cursor-pointer">Établissement vérifié</Label>
                      <Switch
                        checked={facilityForm.is_verified}
                        onCheckedChange={(v) => setFacilityForm(p => ({ ...p, is_verified: v }))}
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 2: Media (Photos, Videos) */}
                <TabsContent value="media" className="mt-0 space-y-4">
                  <div className="bg-amber-50 rounded-lg p-3 text-sm text-amber-800">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    Pour les photos et vidéos, l&apos;établissement doit les gérer depuis son propre dashboard après création.
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Photo de profil (Logo)</Label>
                      <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center">
                        {facilityForm.profile_photo ? (
                          <div className="relative">
                            <img 
                              src={facilityForm.profile_photo} 
                              alt="Profile" 
                              className="w-24 h-24 object-cover rounded-lg mx-auto"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute -top-2 -right-2 h-6 w-6 p-0"
                              onClick={() => setFacilityForm(p => ({ ...p, profile_photo: null }))}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="text-slate-400">
                            <Image className="w-8 h-8 mx-auto mb-2" />
                            <p className="text-xs">Pas de logo</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label>Photo de couverture</Label>
                      <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center">
                        {facilityForm.cover_photo ? (
                          <div className="relative">
                            <img 
                              src={facilityForm.cover_photo} 
                              alt="Cover" 
                              className="w-full h-20 object-cover rounded-lg"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute -top-2 -right-2 h-6 w-6 p-0"
                              onClick={() => setFacilityForm(p => ({ ...p, cover_photo: null }))}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="text-slate-400">
                            <Image className="w-8 h-8 mx-auto mb-2" />
                            <p className="text-xs">Pas de couverture</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label>Vidéo YouTube (URL)</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={facilityForm.youtube_url}
                        onChange={(e) => setFacilityForm(p => ({ ...p, youtube_url: e.target.value }))}
                        placeholder="https://www.youtube.com/watch?v=..."
                      />
                      <Button type="button" variant="outline" size="icon">
                        <Video className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Collez l&apos;URL de la vidéo de présentation YouTube
                    </p>
                  </div>

                  <div>
                    <Label>Galerie photos ({facilityForm.gallery?.length || 0}/20)</Label>
                    <div className="mt-2 grid grid-cols-4 gap-2">
                      {facilityForm.gallery?.map((photo, idx) => (
                        <div key={idx} className="relative">
                          <img 
                            src={photo.url || photo} 
                            alt={`Gallery ${idx}`} 
                            className="w-full h-16 object-cover rounded-lg"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute -top-1 -right-1 h-5 w-5 p-0"
                            onClick={() => removeFromList('gallery', idx)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      {(facilityForm.gallery?.length || 0) < 20 && (
                        <div className="border-2 border-dashed rounded-lg h-16 flex items-center justify-center text-slate-400">
                          <Plus className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 3: Services & Specialties */}
                <TabsContent value="services" className="mt-0 space-y-4">
                  <div>
                    <Label>Spécialités médicales</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={newSpecialty}
                        onChange={(e) => setNewSpecialty(e.target.value)}
                        placeholder="Ex: Cardiologie"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('specialties', newSpecialty, setNewSpecialty))}
                      />
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => addToList('specialties', newSpecialty, setNewSpecialty)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {facilityForm.specialties?.map((spec, idx) => (
                        <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                          {spec}
                          <X 
                            className="w-3 h-3 cursor-pointer hover:text-red-500" 
                            onClick={() => removeFromList('specialties', idx)}
                          />
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Services proposés</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={newService}
                        onChange={(e) => setNewService(e.target.value)}
                        placeholder="Ex: Consultations, Urgences, Radiologie..."
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('services', newService, setNewService))}
                      />
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => addToList('services', newService, setNewService)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {facilityForm.services?.map((svc, idx) => (
                        <Badge key={idx} variant="outline" className="flex items-center gap-1">
                          {svc}
                          <X 
                            className="w-3 h-3 cursor-pointer hover:text-red-500" 
                            onClick={() => removeFromList('services', idx)}
                          />
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Assurances acceptées</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={newInsurance}
                        onChange={(e) => setNewInsurance(e.target.value)}
                        placeholder="Ex: CNPS, AXA, Allianz..."
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('insurance_accepted', newInsurance, setNewInsurance))}
                      />
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => addToList('insurance_accepted', newInsurance, setNewInsurance)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {facilityForm.insurance_accepted?.map((ins, idx) => (
                        <Badge key={idx} className="bg-green-100 text-green-800 flex items-center gap-1">
                          {ins}
                          <X 
                            className="w-3 h-3 cursor-pointer hover:text-red-500" 
                            onClick={() => removeFromList('insurance_accepted', idx)}
                          />
                        </Badge>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 4: Opening Hours */}
                <TabsContent value="hours" className="mt-0 space-y-3">
                  <p className="text-xs sm:text-sm text-slate-500 mb-3">
                    Définissez les horaires d&apos;ouverture pour chaque jour
                  </p>
                  
                  {/* Option Toujours Ouvert (24h/24) */}
                  <div className="p-3 sm:p-4 bg-blue-50 rounded-xl border border-blue-200 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="font-medium text-blue-900 text-sm sm:text-base">Toujours ouvert (24h/24, 7j/7)</Label>
                        <p className="text-xs text-blue-700 mt-1">
                          Activez cette option pour les établissements ouverts en permanence
                        </p>
                      </div>
                      <Switch
                        checked={facilityForm.always_open || false}
                        onCheckedChange={(v) => setFacilityForm(p => ({ ...p, always_open: v }))}
                      />
                    </div>
                  </div>
                  
                  {/* Horaires par jour (masqués si toujours ouvert) */}
                  {!facilityForm.always_open && (
                    <div className="space-y-2">
                      {Object.entries(dayLabels).map(([day, label]) => (
                        <div key={day} className="flex flex-col sm:flex-row sm:items-center gap-2 py-2 border-b">
                          <div className="flex items-center justify-between sm:w-32">
                            <span className="font-medium text-xs sm:text-sm">{label}</span>
                            <div className="flex items-center gap-2 sm:hidden">
                              <Switch
                                checked={!facilityForm.opening_hours?.[day]?.closed}
                                onCheckedChange={(v) => updateOpeningHours(day, 'closed', !v)}
                              />
                              <span className="text-xs text-slate-500 w-12">
                                {facilityForm.opening_hours?.[day]?.closed ? 'Fermé' : 'Ouvert'}
                              </span>
                            </div>
                          </div>
                          <div className="hidden sm:flex items-center gap-2">
                            <Switch
                              checked={!facilityForm.opening_hours?.[day]?.closed}
                              onCheckedChange={(v) => updateOpeningHours(day, 'closed', !v)}
                            />
                            <span className="text-xs text-slate-500 w-12">
                              {facilityForm.opening_hours?.[day]?.closed ? 'Fermé' : 'Ouvert'}
                            </span>
                          </div>
                          {!facilityForm.opening_hours?.[day]?.closed && (
                            <div className="flex items-center gap-2 ml-0 sm:ml-auto">
                              <Input
                                type="time"
                                value={facilityForm.opening_hours?.[day]?.open || '08:00'}
                                onChange={(e) => updateOpeningHours(day, 'open', e.target.value)}
                                className="w-24 sm:w-28 h-8 text-xs sm:text-sm"
                              />
                              <span className="text-slate-400 text-sm">-</span>
                              <Input
                                type="time"
                                value={facilityForm.opening_hours?.[day]?.close || '18:00'}
                                onChange={(e) => updateOpeningHours(day, 'close', e.target.value)}
                                className="w-24 sm:w-28 h-8 text-xs sm:text-sm"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {facilityForm.always_open && (
                    <div className="text-center py-6 text-slate-500">
                      <Clock className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                      <p className="text-sm">Cet établissement est ouvert 24h/24, 7j/7</p>
                    </div>
                  )}
                </TabsContent>

                {/* Submit Buttons (always visible) */}
                <div className="flex gap-2 sm:gap-3 pt-4 border-t sticky bottom-0 bg-white pb-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowFacilityModal(false)}
                    className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    disabled={savingFacility}
                    className="flex-1 h-9 sm:h-10 text-xs sm:text-sm bg-[#E53935] hover:bg-[#C62828]"
                    data-testid="save-facility-btn"
                  >
                    {savingFacility ? (
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    ) : (
                      editingFacilityId ? 'Mettre à jour' : 'Créer'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* KYC Detail Modal - Enhanced with Profile Preview */}
      <Dialog open={showKYCModal} onOpenChange={setShowKYCModal}>
        <DialogContent className="max-w-3xl w-[95vw] sm:w-full max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2 flex-shrink-0 border-b">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <FileCheck className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              Prévisualisation du profil
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Examinez le profil complet avant de valider ou refuser
            </DialogDescription>
          </DialogHeader>

          {selectedKYC && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Tabs */}
              <Tabs value={kycModalTab} onValueChange={setKycModalTab} className="flex-1 flex flex-col overflow-hidden">
                <TabsList className="grid grid-cols-3 mx-4 mt-3 flex-shrink-0">
                  <TabsTrigger value="profile" className="text-xs sm:text-sm">
                    {selectedKYC.user_role === 'facility' ? 'Établissement' : 'Professionnel'}
                  </TabsTrigger>
                  <TabsTrigger value="documents" className="text-xs sm:text-sm">
                    Documents ({selectedKYC.documents?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="action" className="text-xs sm:text-sm">
                    Décision
                  </TabsTrigger>
                </TabsList>

                <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-4">
                  {loadingKYCDetail ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                    </div>
                  ) : (
                    <>
                      {/* Profile Tab */}
                      <TabsContent value="profile" className="mt-4 space-y-4">
                        {/* User Info Header */}
                        <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            selectedKYC.user_role === 'facility' ? 'bg-indigo-100' : 'bg-red-100'
                          }`}>
                            {selectedKYC.user_role === 'facility' ? (
                              <Building2 className="w-7 h-7 text-indigo-600" />
                            ) : (
                              <Stethoscope className="w-7 h-7 text-red-500" />
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-slate-900 text-lg">{selectedKYC.user_name}</h3>
                            <p className="text-sm text-slate-500">{selectedKYC.user_email}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <Badge className={selectedKYC.user_role === 'facility' 
                                ? 'bg-indigo-100 text-indigo-700' 
                                : 'bg-red-100 text-red-600'
                              }>
                                {selectedKYC.user_role === 'facility' ? 'Établissement' : 'Professionnel de santé'}
                              </Badge>
                              <Badge className="bg-amber-100 text-amber-700">
                                En attente de validation
                              </Badge>
                            </div>
                          </div>
                        </div>

                        {/* Facility Profile Preview */}
                        {selectedKYC.user_role === 'facility' && selectedKYC.facility && (
                          <div className="space-y-4">
                            {/* General Info */}
                            <Card className="border-0 shadow-sm">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-indigo-600" />
                                  Informations générales
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <p className="text-slate-500 text-xs">Nom</p>
                                  <p className="font-medium">{selectedKYC.facility.name || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">Type</p>
                                  <p className="font-medium">{facilityTypeLabels[selectedKYC.facility.facility_type] || selectedKYC.facility.facility_type || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">Téléphone</p>
                                  <p className="font-medium">{selectedKYC.facility.phone || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">Email</p>
                                  <p className="font-medium">{selectedKYC.facility.email || '-'}</p>
                                </div>
                                <div className="col-span-2">
                                  <p className="text-slate-500 text-xs">Adresse</p>
                                  <p className="font-medium">{selectedKYC.facility.address || '-'}, {selectedKYC.facility.city || ''}</p>
                                </div>
                                {selectedKYC.facility.description && (
                                  <div className="col-span-2">
                                    <p className="text-slate-500 text-xs">Description</p>
                                    <p className="font-medium text-slate-700">{selectedKYC.facility.description}</p>
                                  </div>
                                )}
                              </CardContent>
                            </Card>

                            {/* Services & Specialties */}
                            {(selectedKYC.facility.specialties?.length > 0 || selectedKYC.facility.services?.length > 0) && (
                              <Card className="border-0 shadow-sm">
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                    <Activity className="w-4 h-4 text-green-600" />
                                    Services proposés
                                  </CardTitle>
                                </CardHeader>
                                <CardContent>
                                  {selectedKYC.facility.specialties?.length > 0 && (
                                    <div className="mb-3">
                                      <p className="text-xs text-slate-500 mb-1">Spécialités</p>
                                      <div className="flex flex-wrap gap-1">
                                        {selectedKYC.facility.specialties.map((s, i) => (
                                          <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {selectedKYC.facility.services?.length > 0 && (
                                    <div>
                                      <p className="text-xs text-slate-500 mb-1">Services</p>
                                      <div className="flex flex-wrap gap-1">
                                        {selectedKYC.facility.services.map((s, i) => (
                                          <Badge key={i} variant="outline" className="text-xs bg-green-50">{s}</Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </CardContent>
                              </Card>
                            )}

                            {/* Opening Hours */}
                            {selectedKYC.facility.opening_hours && (
                              <Card className="border-0 shadow-sm">
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-blue-600" />
                                    Horaires d'ouverture
                                  </CardTitle>
                                </CardHeader>
                                <CardContent>
                                  {selectedKYC.facility.always_open ? (
                                    <Badge className="bg-green-100 text-green-700">Ouvert 24h/24</Badge>
                                  ) : (
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                      {Object.entries(selectedKYC.facility.opening_hours).map(([day, hours]) => (
                                        <div key={day} className="flex justify-between bg-slate-50 px-2 py-1 rounded">
                                          <span className="font-medium capitalize">{day === 'monday' ? 'Lundi' : day === 'tuesday' ? 'Mardi' : day === 'wednesday' ? 'Mercredi' : day === 'thursday' ? 'Jeudi' : day === 'friday' ? 'Vendredi' : day === 'saturday' ? 'Samedi' : 'Dimanche'}</span>
                                          <span className={hours.closed ? 'text-red-500' : 'text-green-600'}>
                                            {hours.closed ? 'Fermé' : `${hours.open} - ${hours.close}`}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </CardContent>
                              </Card>
                            )}

                            {/* Features */}
                            <div className="flex flex-wrap gap-2">
                              {selectedKYC.facility.emergency_24h && (
                                <Badge className="bg-red-100 text-red-700">🚨 Urgences 24h</Badge>
                              )}
                              {selectedKYC.facility.has_ambulance && (
                                <Badge className="bg-blue-100 text-blue-700">🚑 Ambulance</Badge>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Professional Profile Preview */}
                        {selectedKYC.user_role === 'professional' && selectedKYC.professional_profile && (
                          <div className="space-y-4">
                            {/* Professional Info */}
                            <Card className="border-0 shadow-sm">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                  <Stethoscope className="w-4 h-4 text-red-500" />
                                  Informations professionnelles
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <p className="text-slate-500 text-xs">Spécialité</p>
                                  <p className="font-medium">{selectedKYC.professional_profile.specialty || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">Numéro d'ordre</p>
                                  <p className="font-medium">{selectedKYC.professional_profile.order_number || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">Années d'expérience</p>
                                  <p className="font-medium">{selectedKYC.professional_profile.years_experience ? `${selectedKYC.professional_profile.years_experience} ans` : '-'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">Téléphone</p>
                                  <p className="font-medium">{selectedKYC.professional_profile.phone || selectedKYC.user?.phone || '-'}</p>
                                </div>
                                {selectedKYC.professional_profile.bio && (
                                  <div className="col-span-2">
                                    <p className="text-slate-500 text-xs">Biographie</p>
                                    <p className="font-medium text-slate-700">{selectedKYC.professional_profile.bio}</p>
                                  </div>
                                )}
                              </CardContent>
                            </Card>

                            {/* Location */}
                            <Card className="border-0 shadow-sm">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-green-600" />
                                  Lieu d'exercice
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="grid grid-cols-2 gap-3 text-sm">
                                <div className="col-span-2">
                                  <p className="text-slate-500 text-xs">Adresse</p>
                                  <p className="font-medium">{selectedKYC.professional_profile.address || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">Ville</p>
                                  <p className="font-medium">{selectedKYC.professional_profile.city || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">Quartier</p>
                                  <p className="font-medium">{selectedKYC.professional_profile.neighborhood || '-'}</p>
                                </div>
                              </CardContent>
                            </Card>

                            {/* Pricing */}
                            <Card className="border-0 shadow-sm">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                  <Wallet className="w-4 h-4 text-amber-600" />
                                  Tarifs
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="grid grid-cols-3 gap-3 text-sm">
                                <div className="bg-slate-50 p-3 rounded-lg text-center">
                                  <p className="text-slate-500 text-xs">Consultation</p>
                                  <p className="font-bold text-lg">{selectedKYC.professional_profile.consultation_fee?.toLocaleString() || '-'}</p>
                                  <p className="text-xs text-slate-400">FCFA</p>
                                </div>
                                <div className="bg-slate-50 p-3 rounded-lg text-center">
                                  <p className="text-slate-500 text-xs">Domicile</p>
                                  <p className="font-bold text-lg">{selectedKYC.professional_profile.home_visit_fee?.toLocaleString() || '-'}</p>
                                  <p className="text-xs text-slate-400">FCFA</p>
                                </div>
                                <div className="bg-slate-50 p-3 rounded-lg text-center">
                                  <p className="text-slate-500 text-xs">Vidéo</p>
                                  <p className="font-bold text-lg">{selectedKYC.professional_profile.video_fee?.toLocaleString() || '-'}</p>
                                  <p className="text-xs text-slate-400">FCFA</p>
                                </div>
                              </CardContent>
                            </Card>

                            {/* Availability */}
                            {selectedKYC.professional_profile.availability && (
                              <Card className="border-0 shadow-sm">
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-blue-600" />
                                    Disponibilités
                                  </CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    {Object.entries(selectedKYC.professional_profile.availability).map(([day, slots]) => (
                                      <div key={day} className="flex justify-between bg-slate-50 px-2 py-1 rounded">
                                        <span className="font-medium capitalize">{day === 'monday' ? 'Lundi' : day === 'tuesday' ? 'Mardi' : day === 'wednesday' ? 'Mercredi' : day === 'thursday' ? 'Jeudi' : day === 'friday' ? 'Vendredi' : day === 'saturday' ? 'Samedi' : 'Dimanche'}</span>
                                        <span className={!slots || slots.length === 0 ? 'text-red-500' : 'text-green-600'}>
                                          {!slots || slots.length === 0 ? 'Non disponible' : `${slots.length} créneau(x)`}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </CardContent>
                              </Card>
                            )}

                            {/* Services */}
                            {selectedKYC.professional_profile.services_offered?.length > 0 && (
                              <Card className="border-0 shadow-sm">
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-sm font-semibold">Services proposés</CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <div className="flex flex-wrap gap-1">
                                    {selectedKYC.professional_profile.services_offered.map((s, i) => (
                                      <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                                    ))}
                                  </div>
                                </CardContent>
                              </Card>
                            )}
                          </div>
                        )}

                        {/* No profile data available */}
                        {!selectedKYC.facility && !selectedKYC.professional_profile && (
                          <div className="text-center py-8 text-slate-500">
                            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                            <p>Aucune information de profil disponible</p>
                            <p className="text-sm mt-1">L'utilisateur n'a pas encore configuré son profil</p>
                          </div>
                        )}
                      </TabsContent>

                      {/* Documents Tab */}
                      <TabsContent value="documents" className="mt-4">
                        <div className="space-y-3">
                          {selectedKYC.documents && selectedKYC.documents.length > 0 ? (
                            selectedKYC.documents.map((doc, idx) => {
                              const fileName = doc.name || doc.file_name || 'Document';
                              const fileUrl = doc.url || '';
                              const isPDF = doc.is_pdf || fileUrl.toLowerCase().includes('.pdf') || fileName.toLowerCase().endsWith('.pdf');
                              const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl) || /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
                              const iframeUrl = isPDF ? `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true` : fileUrl;
                              
                              return (
                                <div key={idx} className="border rounded-lg p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                        isPDF ? 'bg-red-100' : 'bg-blue-100'
                                      }`}>
                                        <FileText className={`w-5 h-5 ${isPDF ? 'text-red-600' : 'text-blue-600'}`} />
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">{getDocumentTypeLabel(doc.document_type || doc.type)}</p>
                                        <p className="text-xs text-slate-500">{fileName}</p>
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => window.open(isPDF ? iframeUrl.replace('&embedded=true', '') : fileUrl, '_blank')}
                                      >
                                        <Eye className="w-4 h-4 mr-1" />
                                        Voir
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={async () => {
                                          try {
                                            const response = await fetch(fileUrl);
                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.download = fileName;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            window.URL.revokeObjectURL(url);
                                          } catch (error) {
                                            window.open(fileUrl, '_blank');
                                          }
                                        }}
                                      >
                                        <Download className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* Preview */}
                                  {isImage && fileUrl && (
                                    <div className="rounded-lg overflow-hidden border">
                                      <img src={fileUrl} alt={fileName} className="w-full max-h-48 object-contain bg-slate-100" />
                                    </div>
                                  )}
                                  {isPDF && fileUrl && (
                                    <div className="rounded-lg overflow-hidden border bg-slate-100">
                                      <iframe src={iframeUrl} className="w-full h-64" title={fileName} frameBorder="0" />
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="text-center py-12 text-slate-500 border rounded-lg">
                              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                              <p>Aucun document soumis</p>
                            </div>
                          )}
                        </div>
                      </TabsContent>

                      {/* Action Tab */}
                      <TabsContent value="action" className="mt-4 space-y-4">
                        {/* Summary */}
                        <Card className="border-amber-200 bg-amber-50">
                          <CardContent className="p-4">
                            <h4 className="font-semibold text-amber-800 mb-2">Résumé de la demande</h4>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-amber-600">Type :</span>
                                <span className="ml-2 font-medium">{selectedKYC.user_role === 'facility' ? 'Établissement' : 'Professionnel'}</span>
                              </div>
                              <div>
                                <span className="text-amber-600">Documents :</span>
                                <span className="ml-2 font-medium">{selectedKYC.documents?.length || 0}</span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-amber-600">Soumis le :</span>
                                <span className="ml-2 font-medium">
                                  {new Date(selectedKYC.submitted_at || selectedKYC.created_at).toLocaleDateString('fr-FR', { 
                                    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                                  })}
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Reject reason */}
                        <div>
                          <Label htmlFor="reject-reason" className="text-sm font-medium">
                            Motif de refus (obligatoire si rejet)
                          </Label>
                          <Textarea
                            id="reject-reason"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Indiquez le motif si vous rejetez la demande..."
                            className="mt-2"
                            rows={3}
                          />
                        </div>

                        {/* Action buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                          <Button
                            variant="outline"
                            onClick={() => setShowKYCModal(false)}
                            className="w-full sm:flex-1"
                          >
                            Annuler
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleKYCReview('rejected')}
                            disabled={!rejectReason.trim()}
                            className="w-full sm:flex-1 border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                            data-testid="reject-kyc-btn"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Refuser
                          </Button>
                          <Button
                            onClick={() => handleKYCReview('approved')}
                            className="w-full sm:flex-1 bg-emerald-600 hover:bg-emerald-700"
                            data-testid="approve-kyc-btn"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Approuver
                          </Button>
                        </div>
                      </TabsContent>
                    </>
                  )}
                </div>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
